import { useFormation } from '@contexts/FormationContext/FormationContext';
import { showToast } from '@design-system';
import { useGetFormationsMutation, useGetUsersMutation, useUploadFileMutation, usePostFormationResultsMutation } from '@services';
import { transformSignatureToFile } from '@utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

export const useFormationTalkEmargementPageVM = () => {
    const { categoryId, formationId } = useParams();
    const navigate = useNavigate();
    const { setSignature, setRequestUser, requestUser, signature, questionsAnswers } = useFormation();

    const [errorMessage, setErrorMessage] = useState<string>('');
    const [pageTitle, setPageTitle] = useState<string>('');
    const [isEmpty, setIsEmpty] = useState<boolean>(true);
    const [isSomeoneElse, setIsSomeoneElse] = useState<boolean>(false);
    const [instructorName, setInstructorName] = useState<string>('');
    const signatureCanvasRef = useRef<SignatureCanvas>(null);
    const [usersOptions, setUsersOptions] = useState<Array<{ id: string; name: string }>>([]);

    const [getFormations, { isLoading: isLoadingFormations, data: formations }] = useGetFormationsMutation();
    const [uploadFile, { isError: isFileUploadError, error: fileUploadError, data: fileUploadData }] = useUploadFileMutation();
    const [getUsers, { data: users }] = useGetUsersMutation();
    const [postFormationResults, { isLoading: isLoadingPostResults, data: postResultsData, error: postResultsError }] =
        usePostFormationResultsMutation();

    const handleIsSomeoneElseChange = (checked: boolean) => {
        setIsSomeoneElse(checked);
    };

    const handleRequestUserChange = (value: string) => {
        setRequestUser(value);
    };

    const handleInstructorNameChange = (value: string) => {
        setInstructorName(value);
    };

    const handleClearSignature = () => {
        if (signatureCanvasRef.current) {
            signatureCanvasRef.current.clear();
            setIsEmpty(true);
            setSignature('');
        }
    };

    const handleSaveSignature = () => {
        if (signatureCanvasRef.current) {
            const isEmpty = signatureCanvasRef.current.isEmpty();
            if (isEmpty) {
                showToast({
                    message: 'Veuillez dessiner votre signature avant de continuer',
                    type: 'error'
                });
                return;
            }

            const dataUrl = signatureCanvasRef.current.toDataURL('image/png');
            const file = transformSignatureToFile(dataUrl);

            const formData = new FormData();
            formData.append('file', file);

            uploadFile(formData);
        }
    };

    const handleClickSave = () => {
        if (!instructorName) {
            showToast({
                message: 'Veuillez remplir tous les champs obligatoires',
                type: 'error'
            });
            return;
        }

        if (isSomeoneElse && !isEmpty) {
            handleSaveSignature();
        } else if (isSomeoneElse && isEmpty) {
            showToast({
                message: 'Veuillez signer avant de continuer',
                type: 'error'
            });
        } else {
            if (!formationId) return;

            postFormationResults({
                formationId: parseInt(formationId),
                request_user_id: parseInt(requestUser ?? ''),
                answers: questionsAnswers.map((question) => ({
                    question_id: question.questionId,
                    answer_id: question.answerId
                })),
                ...(signature && { signature }),
                ...(instructorName && { instructor_name: instructorName }),
                ...(requestUser && { request_user: requestUser })
            });
        }
    };

    const isDisabled = useMemo(
        () => (isSomeoneElse && (isEmpty || !requestUser)) || !instructorName,
        [isSomeoneElse, isEmpty, requestUser, instructorName]
    );

    useEffect(() => {
        if (!formationId) return;
        getFormations({ categoryId: parseInt(categoryId ?? '0') });
        getUsers(undefined);
    }, [formationId, getFormations, getUsers, categoryId]);

    useEffect(() => {
        if (!formations || !formationId) return;

        const currentFormation = formations.find((formation) => formation.id === parseInt(formationId));

        if (!currentFormation) {
            setErrorMessage('Formation non trouvée');
            return;
        }

        setPageTitle(currentFormation.title);
    }, [formations, formationId]);

    useEffect(() => {
        if (users) {
            const allUsers = users.operators.flatMap((operator) => operator.worksites.flatMap(() => []));
            setUsersOptions(allUsers);
        }
    }, [users]);

    useEffect(() => {
        if (isFileUploadError && fileUploadError) {
            console.error('fileUploadError:', fileUploadError);
        }
        if (fileUploadData) {
            setSignature(fileUploadData.original);
            if (!formationId || !requestUser) return;

            postFormationResults({
                formationId: parseInt(formationId),
                request_user_id: parseInt(requestUser ?? ''),
                answers: questionsAnswers.map((question) => ({
                    question_id: question.questionId,
                    answer_id: question.answerId
                })),
                signature: fileUploadData.original,
                ...(instructorName && { instructor_name: instructorName })
            });
        }
    }, [
        fileUploadData,
        isFileUploadError,
        fileUploadError,
        setSignature,
        formationId,
        postFormationResults,
        instructorName,
        questionsAnswers,
        requestUser
    ]);

    useEffect(() => {
        if (isLoadingPostResults) {
            return;
        }

        if (postResultsError) {
            setErrorMessage('Une erreur est survenue lors de la soumission des réponses');
            return;
        }

        if (postResultsData) {
            showToast({ type: 'success', message: 'Formation validée avec succès' });
            navigate('/sante-securite/formations/list');
        }
    }, [isLoadingPostResults, postResultsError, postResultsData, navigate]);

    useEffect(() => {
        if (!isSomeoneElse) {
            setIsEmpty(true);
        }
    }, [isSomeoneElse]);

    return {
        errorMessage,
        pageTitle,
        isLoadingFormations,
        formations,
        isSomeoneElse,
        handleIsSomeoneElseChange,
        handleClearSignature,
        handleSaveSignature,
        isEmpty,
        signatureCanvasRef,
        setIsEmpty,
        handleClickSave,
        isDisabled,
        usersOptions,
        requestUser,
        handleRequestUserChange,
        instructorName,
        handleInstructorNameChange
    };
};
